import { useQuery } from "@tanstack/react-query";

import { env } from "@/web/lib/env";
import { fetchPlaceDetailsByPlaceId } from "@/web/lib/googlePlaces/fetchPlaceFromPrediction";

type PhotoLike = google.maps.places.PlacePhoto & { name?: string };

function resolvePhotoUrl(photo: PhotoLike, maxWidth: number): string | null {
  if (typeof photo.getURI === "function") return photo.getURI({ maxWidth });
  const name = photo.name;
  const key = env.VITE_GAPI_PLACES_KEY;
  if (name && key) {
    return `https://places.googleapis.com/v1/${name}/media?key=${key}&maxWidthPx=${maxWidth}`;
  }
  return null;
}

/**
 * First hero-style photo URL for a `Place` returned from `fetchFields({ fields: ["photos"] })`.
 * Prefers the JS SDK `PlacePhoto.getURI`; falls back to Places API v1 media URLs for plain `name` objects.
 */
export function getFirstPlacePhotoUrl(
  place: {
    photos?: google.maps.places.PlacePhoto[] | null;
  },
  maxWidth = 800,
): string | null {
  const photo = place.photos?.[0];
  return photo ? resolvePhotoUrl(photo as PhotoLike, maxWidth) : null;
}

/** Up to `limit` photo URLs with stable resource names; filters out photos that can't be resolved. */
export function getPlacePhotoUrls(
  place: { photos?: google.maps.places.PlacePhoto[] | null },
  limit: number,
  maxWidth = 800,
): { url: string; resourceName: string | null }[] {
  const photos = place.photos ?? [];
  const out: { url: string; resourceName: string | null }[] = [];
  for (const p of photos) {
    if (out.length >= limit) break;
    const url = resolvePhotoUrl(p as PhotoLike, maxWidth);
    if (url) out.push({ url, resourceName: (p as PhotoLike).name ?? null });
  }
  return out;
}

/**
 * Resolves a stable Google Places photo resource name (e.g.
 *   `places/ChIJ.../photos/AeY9eOjkP-...`) into a renderable v1 media URL,
 *   using our public, HTTP-referrer-restricted Places API key. Returns
 *   `null` if no key is configured or the name is empty.
 *
 * Use this on the read side when the only thing we have stored on the server
 *   is the photo resource name (the recommended way to persist Places
 *   photos — URLs from `PlacePhoto.getURI` are short-lived and shouldn't be
 *   cached).
 */
export function resolvePlacePhotoUrlByName(
  name: string | null | undefined,
  maxWidth = 800,
): string | null {
  if (!name) return null;
  const key = env.VITE_GAPI_PLACES_KEY;
  if (!key) return null;
  return `https://places.googleapis.com/v1/${name}/media?key=${key}&maxWidthPx=${maxWidth}`;
}

/**
 * Resolves the first place photo URL from a Google Place ID alone, by fetching
 *   place details (`photos` field) via the JS SDK and taking the first photo.
 *   Returns `{ url: null, isPending }` while loading or when no place id / photo
 *   is available.
 *
 * Use this when the only durable identifier we have stored is the Google Place
 *   ID — e.g. operating properties discovered by Athena, which never returns a
 *   photo resource name. The fetch is cached by react-query keyed on the place
 *   id, so multiple cards for the same place share one network round-trip.
 */
export function usePlacePhotoUrlByPlaceId(
  placeId: string | null | undefined,
  maxWidth = 800,
): { url: string | null; isPending: boolean } {
  const query = useQuery({
    queryKey: ["googlePlacePhotoUrl", placeId, maxWidth] as const,
    queryFn: async () => {
      if (!placeId) throw new Error("placeId required");
      const place = await fetchPlaceDetailsByPlaceId(placeId);
      return getFirstPlacePhotoUrl(place, maxWidth);
    },
    enabled: Boolean(placeId),
    staleTime: Infinity,
  });

  return { url: query.data ?? null, isPending: query.isPending && Boolean(placeId) };
}
